export const sanitizeCustomTags = (
  content: string,
  customTags: string[] = [
    "<br-cbb>",
    "<links-cbb>",
    "</links-cbb>",
    "<clickable-options-cbb>",
    "</clickable-options-cbb>",
    "<handoff>",
    "<ecommerce-image>",
    "</ecommerce-image>",
  ],
): string => {
  if (!content || !content.trim()) return "";
  if (content.includes("<invoke") && !content.includes("</invoke>")) {
    return "";
  }
  if (
    content.includes("<function_calls") &&
    !content.includes("</function_calls>")
  ) {
    return "";
  }
  if (
    content.includes("<Tools_Results") &&
    !content.includes("</Tools_Results>")
  ) {
    return "";
  }
  if (
    content.includes("<reasoning-cbb") &&
    !content.includes("</reasoning-cbb>")
  ) {
    return "";
  }

  let truncateIndex = content.length;
  for (const tag of customTags) {
    const index = content.toLowerCase().indexOf(tag);
    if (index !== -1 && index > 0 && index < truncateIndex) {
      truncateIndex = index;
    }
  }

  let result = content.substring(0, truncateIndex);

  customTags.forEach((tag) => {
    result = result.replace(
      new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
      "",
    );
  });

  // Only remove <li> tags within custom tags (clickable-options-cbb and links-cbb)
  result = result.replace(
    /<clickable-options-cbb>([\s\S]*?)<\/clickable-options-cbb>/gi,
    (match, content) => {
      return content.replace(/<li>|<\/li>/g, "");
    },
  );
  result = result.replace(
    /<links-cbb>([\s\S]*?)<\/links-cbb>/gi,
    (match, content) => {
      return content.replace(/<li>|<\/li>/g, "");
    },
  );

  result = result.replace(/<invoke[^>]*>[\s\S]*<\/?invoke[^>]*>/gi, "");
  result = result.replace(/<function[^>]*>[\s\S]*<\/?function[^>]*>/gi, "");
  result = result.replace(/<tool[^>]*>[\s\S]*<\/?tool[^>]*>/gi, "");
  result = result.replace(
    /<reasoning-cbb[^>]*>[\s\S]*<\/?reasoning-cbb[^>]*>/gi,
    "",
  );
  // Remove tool information like {"tool_name": "product_search", "tool_id": 0} or {"tool_name": "product_search", "tool_id": "1/2/3"}
  result = result.replace(
    /\{"tool_name":\s*"[^"]*",\s*"tool_id":\s*(?:\d+|"[^"]*")\}/g,
    "",
  );

  result = result.replace(/<\/?br-cbb>/gi, " ");
  result = result.replace(/<\/?strong>/gi, "**");
  result = result.replace(/<\/?result[^>]*>/gi, "");
  result = result.replace(/<\/?fallback[^>]*>/gi, "");

  // Remove tool information like {"tool_name": "product_search", "tool_id": 0} or {"tool_name": "product_search", "tool_id": "1/2/3"}
  result = result.replace(
    /\{"tool_name":\s*"[^"]*",\s*"tool_id":\s*(?:\d+|"[^"]*")\}/g,
    "",
  );

  return result;
};

export const extractSeperationData = (content, tagName = "br-cbb") => {
  const tag = `<${tagName}>`;
  const pattern = new RegExp(tag, "g");
  const matches = content.split(pattern);

  const filteredMatches = matches
    .map((match) => sanitizeCustomTags(match))
    .filter((match) => match.trim() !== "");
  return filteredMatches.slice(1);
};

export const extractClickableOptionsData = (content, tagName) => {
  if (!content || typeof content !== "string") return null;

  const pattern = new RegExp(`<${tagName}[^>]*>(.*?)<\/${tagName}>`, "s");
  const match = content.match(pattern);

  if (!match || !match[1]) {
    return null;
  }

  const liPattern = /<li[^>]*>(.*?)<\/li>/g;
  const liMatches = [...match[1].matchAll(liPattern)];

  if (liMatches.length === 0) {
    return null;
  }

  return liMatches.map((match) => sanitizeCustomTags(match[1].trim()));
};

export const extractMediaLinks = (content: string): string[] => {
  if (!content || typeof content !== "string") return [];

  const pattern = /<links-cbb[^>]*>(.*?)<\/links-cbb>/s;
  const match = content.match(pattern);

  if (!match || !match[1]) {
    return [];
  }

  const liPattern = /<li[^>]*>(.*?)<\/li>/g;
  const liMatches = [...match[1].matchAll(liPattern)];

  return liMatches.map((match) => match[1].trim()).filter((link) => link);
};

export const isHtml = (text: string): boolean => {
  if (!text) return false;
  const htmlRegex = /<[a-z][\s\S]*>/i;
  return htmlRegex.test(text);
};

export const renderHTML = (html: string) => {
  return { __html: sanitizeCustomTags(html || "") };
};

export const removeCustomTags = (
  content: string,
  customTags: string[] = [
    "<br-cbb>",
    "<links-cbb>",
    "</links-cbb>",
    "<clickable-options-cbb>",
    "</clickable-options-cbb>",
    "<handoff>",
    "<ecommerce-image>",
    "</ecommerce-image>",
  ],
): string => {
  if (!content || !content.trim()) return "";

  let result = content;

  customTags.forEach((tag) => {
    const escapedTag = tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    result = result.replace(new RegExp(escapedTag, "gi"), "");
  });

  result = result.replace(/<\/?li>/gi, "");
  result = result.replace(/<br-cbb>/gi, " ");

  return result.trim();
};

export const isEcommerceImage = (content: string): boolean => {
  if (!content || typeof content !== "string") return false;
  return content.includes("<ecommerce-image>");
};

export const isEcommerceImageComplete = (content: string): boolean => {
  if (!content || typeof content !== "string") return false;
  // Check if both opening and closing tags are present
  return (
    content.includes("<ecommerce-image>") &&
    content.includes("</ecommerce-image>")
  );
};

export const removeEcommerceContent = (content: string): string => {
  if (!content || typeof content !== "string") return content;

  // Remove complete ecommerce-image blocks
  let result = content.replace(
    /<ecommerce-image[^>]*>[\s\S]*?<\/ecommerce-image>/g,
    "",
  );

  // Also remove incomplete ecommerce-image blocks (streaming)
  // This handles cases where opening tag exists but closing tag hasn't arrived yet
  result = result.replace(/<ecommerce-image[^>]*>[\s\S]*$/g, "");

  return result;
};

export const extractEcommerceImageContent = (
  content: string,
): string | null => {
  if (!content || typeof content !== "string") return null;

  const pattern = /<ecommerce-image[^>]*>(.*?)<\/ecommerce-image>/s;
  const match = content.match(pattern);

  if (!match || !match[1]) {
    return null;
  }

  return match[1].trim();
};

export interface EcommerceProduct {
  name: string;
  image: string;
  link: string;
  price: string;
  description?: string;
}

/**
 * Parse ecommerce product data from <ecommerce-image> content
 * Expected format:
 * - ![Product Name](image_url)
 *   **[Product Name](product_link)** - $price
 *   Brief description
 */
export const parseEcommerceProducts = (content: string): EcommerceProduct[] => {
  if (!content || typeof content !== "string") return [];

  const ecommerceContent = extractEcommerceImageContent(content);
  if (!ecommerceContent) return [];

  const products: EcommerceProduct[] = [];

  // Split by list items (lines starting with "- ")
  const productBlocks = ecommerceContent
    .split(/\n-\s+/)
    .filter((block) => block.trim());

  for (const block of productBlocks) {
    try {
      const lines = block
        .trim()
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line);

      if (lines.length === 0) continue;

      // Extract image: ![alt](url)
      const imageMatch = lines[0].match(/!\[([^\]]*)\]\(([^)]+)\)/);
      if (!imageMatch) continue;

      const imageName = imageMatch[1];
      const imageUrl = imageMatch[2];

      // Extract product name and link: **[name](link)** - $price
      let productName = imageName;
      let productLink = "";
      let price = "";
      let description = "";

      // Look for the link and price in the remaining lines
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Match: **[Product Name](link)** - $price
        const linkPriceMatch = line.match(
          /\*\*\[([^\]]+)\]\(([^)]+)\)\*\*\s*-\s*(.+)/,
        );
        if (linkPriceMatch) {
          productName = linkPriceMatch[1];
          productLink = linkPriceMatch[2];
          price = linkPriceMatch[3].trim();

          // Remaining lines are description
          if (i + 1 < lines.length) {
            description = lines
              .slice(i + 1)
              .join(" ")
              .trim();
          }
          break;
        }

        // Alternative format: [Product Name](link) - $price (without bold)
        const simpleLinkMatch = line.match(
          /\[([^\]]+)\]\(([^)]+)\)\s*-\s*(.+)/,
        );
        if (simpleLinkMatch) {
          productName = simpleLinkMatch[1];
          productLink = simpleLinkMatch[2];
          price = simpleLinkMatch[3].trim();

          if (i + 1 < lines.length) {
            description = lines
              .slice(i + 1)
              .join(" ")
              .trim();
          }
          break;
        }
      }

      // If no link found, try to extract from any line
      if (!productLink) {
        for (const line of lines) {
          const anyLinkMatch = line.match(/\[([^\]]+)\]\(([^)]+)\)/);
          if (anyLinkMatch) {
            productLink = anyLinkMatch[2];
            break;
          }
        }
      }

      products.push({
        name: productName,
        image: imageUrl,
        link: productLink,
        price: price || "Price not available",
        description: description || undefined,
      });
    } catch (error) {
      console.error("Error parsing product block:", error);
      continue;
    }
  }

  return products;
};
